import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORTED_CURRENCIES } from './Settings';

export function WorkspaceSetup() {
  const { currentWorkspace, updateWorkspaceSettings } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Branding
    companyName: currentWorkspace?.settings?.branding?.companyName || '',
    address: currentWorkspace?.settings?.branding?.address || '',
    email: currentWorkspace?.settings?.branding?.email || '',
    phone: currentWorkspace?.settings?.branding?.phone || '',
    licenseNumber: currentWorkspace?.settings?.branding?.licenseNumber || '',
    city: currentWorkspace?.settings?.branding?.city || '',
    postalCode: currentWorkspace?.settings?.branding?.postalCode || '',
    mobile: currentWorkspace?.settings?.branding?.mobile || '',
    website: currentWorkspace?.settings?.branding?.website || '',
    taxId: currentWorkspace?.settings?.branding?.taxId || '',
    primaryColor: currentWorkspace?.settings?.branding?.primaryColor || '',
    
    // Basic Settings
    currency: currentWorkspace?.settings?.currency || '',
    taxRate: currentWorkspace?.settings?.taxRate || 0,
    invoicePrefix: currentWorkspace?.settings?.invoicePrefix || '',
  });

  // Update formData when currentWorkspace changes
  useEffect(() => {
    if (currentWorkspace?.settings?.branding) {
      setFormData({
        companyName: currentWorkspace.settings.branding.companyName || '',
        address: currentWorkspace.settings.branding.address || '',
        email: currentWorkspace.settings.branding.email || '',
        phone: currentWorkspace.settings.branding.phone || '',
        licenseNumber: currentWorkspace.settings.branding.licenseNumber || '',
        city: currentWorkspace.settings.branding.city || '',
        postalCode: currentWorkspace.settings.branding.postalCode || '',
        mobile: currentWorkspace.settings.branding.mobile || '',
        website: currentWorkspace.settings.branding.website || '',
        taxId: currentWorkspace.settings.branding.taxId || '',
        primaryColor: currentWorkspace.settings.branding.primaryColor || '',
        currency: currentWorkspace.settings.currency || '',
        taxRate: currentWorkspace.settings.taxRate || 0,
        invoicePrefix: currentWorkspace.settings.invoicePrefix || 'INV',
      });
    }
  }, [currentWorkspace]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentWorkspace) return;

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Logo = event.target?.result as string;
        await updateWorkspaceSettings({
          branding: {
            ...currentWorkspace.settings.branding,
            logo: base64Logo
          }
        });
        setFormData(prev => ({
          ...prev,
          logo: base64Logo
        }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo. Please try again.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateWorkspaceSettings({
        currency: formData.currency,
        taxRate: Number(formData.taxRate),
        invoicePrefix: formData.invoicePrefix,
        branding: {
          companyName: formData.companyName,
          address: formData.address,
          email: formData.email,
          phone: formData.phone,
          mobile: formData.mobile,
          website: formData.website,
          licenseNumber: formData.licenseNumber,
          taxId: formData.taxId,
          city: formData.city,
          postalCode: formData.postalCode,
          primaryColor: formData.primaryColor,
          logo: currentWorkspace?.settings?.branding?.logo
        }
      });
      alert('Business information updated successfully!');
    } catch (error) {
      console.error('Error updating business information:', error);
      alert('Failed to update business information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-6">Business Information</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Logo */}
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              {currentWorkspace?.settings?.branding?.logo ? (
                <img 
                  src={currentWorkspace.settings.branding.logo} 
                  alt="Company Logo" 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-gray-400 text-sm text-center">No Logo</div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address *
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Number
                </label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax ID
                </label>
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Settings</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {SUPPORTED_CURRENCIES.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name} ({currency.symbol})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  name="taxRate"
                  value={formData.taxRate}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice Prefix
                </label>
                <input
                  type="text"
                  name="invoicePrefix"
                  value={formData.invoicePrefix}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brand Color
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    className="w-12 h-12 p-1 rounded border border-gray-300"
                  />
                  <input
                    type="text"
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Updating...' : 'Update Business Information'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 